import React from 'react';
import {
  Box,
  Divider,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import { useTheme } from '@mui/material/styles';

export interface DataRow {
  [key: string]: string;
}
export type Order = 'asc' | 'desc';
interface AnalyticsTableProps {
  columnHeaders: string[];
  dataRows: DataRow[];
  dataRowKeys: string[];
  rowCount: number;

  page: number;
  setPage(index: number): any;
  pageSize: number;
  setPageSize(size: number): any;
  order: Order;
  setOrder(order: Order): any;
  orderBy: string;
  setOrderBy(orderBy: string): any;
  isLoading: boolean;
}

const getKeyValue = (obj: DataRow, key: string) => {
  const keys = key.split('.');
  let value: DataRow | string = obj;
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i];
    if (typeof value != 'string') {
      value = value?.[k];
    }
  }
  if (typeof value == 'string') {
    return value;
  }
  return '';
};
const AnalyticsTable: React.FC<AnalyticsTableProps> = ({
  columnHeaders,
  dataRows,
  dataRowKeys,
  rowCount,
  page,
  setPage,
  pageSize,
  setPageSize,
  order,
  setOrder,
  orderBy,
  setOrderBy,
  isLoading,
}) => {
  const theme = useTheme();
  const columnWidth = 908 / dataRowKeys.length - 1; // Actual width of table

  const handleSortClick = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const tableRowsLoader = () => {
    return [...Array(pageSize)].map((row, rowIndex) => (
      <TableRow key={rowIndex}>
        {[...Array(dataRowKeys.length)].map((rowKey, columnIndex) => (
          <TableCell
            key={rowKey}
            component='th'
            scope='row'
            sx={{
              minWidth: columnWidth,
              maxWidth: columnWidth,
              verticalAlign: 'middle',
              fontSize: '16px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            <Skeleton animation='wave' variant='text' />
          </TableCell>
        ))}
      </TableRow>
    ));
  };
  return (
    <Box sx={{ flexDirection: 'column' }}>
      <TableContainer sx={{ border: `1px solid ${theme.palette.divider}` }}>
        <Table>
          <TableHead>
            <TableRow key={1} sx={{ border: `1px solid ${theme.palette.divider}` }}>
              {columnHeaders.map((columnHeader: string, index: number) => (
                <TableCell
                  key={dataRowKeys[index]}
                  sx={{
                    minWidth: columnWidth,
                    maxWidth: columnWidth,
                    paddingRight: 0,
                    verticalAlign: 'top',
                    fontSize: '16px',
                  }}
                >
                  <TableSortLabel
                    active={orderBy === dataRowKeys[index]}
                    direction={orderBy === dataRowKeys[index] ? order : 'asc'}
                    onClick={() => handleSortClick(dataRowKeys[index])}
                    sx={{
                      width: '100%',
                      height: 16,
                      borderRight: `${index === dataRowKeys.length - 1 ? 0 : 2}px solid ${
                        theme.palette.divider
                      }`,
                    }}
                  >
                    <Typography sx={{ textWrap: 'nowrap' }}>{columnHeader}</Typography>
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? tableRowsLoader()
              : dataRows.map((row: DataRow, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {dataRowKeys.map((keyName: string, columnIndex) => (
                      <TableCell
                        key={keyName}
                        sx={{
                          minWidth: columnWidth,
                          maxWidth: columnWidth,
                          verticalAlign: 'middle',
                          fontSize: '16px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {getKeyValue(row, keyName)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[20, 50, 100]}
        component='div'
        count={rowCount}
        rowsPerPage={pageSize}
        page={page}
        onPageChange={(event: unknown, newPage: number) => setPage(newPage)}
        onRowsPerPageChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          setPageSize(parseInt(event.target.value));
          setPage(0);
        }}
      />
    </Box>
  );
};

export default AnalyticsTable;
